/**
 * Saved-view presets (§21) persisted in localStorage. A saved view is a named
 * set of queue filter predicates (no pagination). Pure storage logic with an
 * injectable Storage so it's unit-testable in the node vitest project (a tiny
 * in-memory Storage stub is enough — no DOM required).
 */
import { MULTI_KEYS, type ViewFilter } from "./filters.js";

export interface SavedView {
  id: string;
  name: string;
  filter: ViewFilter;
}

// v2: predicates became multi-value (string[]) in §A. Bumping the key drops any
// incompatible v1 (scalar) views rather than mis-reading them.
const STORAGE_KEY = "cairn.savedViews.v2";

/**
 * Minimal Storage surface we depend on (getItem/setItem). `window.localStorage`
 * satisfies it; tests pass a stub. Kept narrow so this module never references
 * the DOM `Storage` lib type directly.
 */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function isStringArray(v: unknown): boolean {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

function isViewFilter(v: unknown): v is ViewFilter {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  // Multi-value predicates must be string[] when present (all optional).
  for (const k of MULTI_KEYS) {
    if (o[k] !== undefined && !isStringArray(o[k])) return false;
  }
  // Date bounds must be strings when present.
  for (const k of ["createdFrom", "createdTo"]) {
    if (o[k] !== undefined && typeof o[k] !== "string") return false;
  }
  return true;
}

function isSavedView(v: unknown): v is SavedView {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return typeof o.id === "string" && typeof o.name === "string" && isViewFilter(o.filter);
}

/** Read & validate the saved views; tolerant of corrupt/missing storage. */
export function loadSavedViews(store: KeyValueStore): SavedView[] {
  const raw = store.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedView);
  } catch {
    return [];
  }
}

function persist(store: KeyValueStore, views: SavedView[]): void {
  store.setItem(STORAGE_KEY, JSON.stringify(views));
}

/** Generate a collision-resistant id without depending on crypto/DOM. */
function newId(): string {
  return `view_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Upsert a named view. A blank name is rejected (returns the list unchanged).
 * If a view with the same (case-insensitive) name exists, its filter is
 * replaced; otherwise a new view is appended. Returns the new list.
 */
export function upsertSavedView(
  store: KeyValueStore,
  name: string,
  filter: ViewFilter,
): SavedView[] {
  const trimmed = name.trim();
  if (!trimmed) return loadSavedViews(store);

  const views = loadSavedViews(store);
  const idx = views.findIndex((v) => v.name.toLowerCase() === trimmed.toLowerCase());
  const next: SavedView[] =
    idx >= 0
      ? views.map((v, i) => (i === idx ? { ...v, name: trimmed, filter } : v))
      : [...views, { id: newId(), name: trimmed, filter }];
  persist(store, next);
  return next;
}

/** Rename a saved view by id (blank name ignored). Returns the new list. */
export function renameSavedView(store: KeyValueStore, id: string, name: string): SavedView[] {
  const trimmed = name.trim();
  if (!trimmed) return loadSavedViews(store);
  const next = loadSavedViews(store).map((v) => (v.id === id ? { ...v, name: trimmed } : v));
  persist(store, next);
  return next;
}

/** Delete a saved view by id. Returns the new list. */
export function deleteSavedView(store: KeyValueStore, id: string): SavedView[] {
  const next = loadSavedViews(store).filter((v) => v.id !== id);
  persist(store, next);
  return next;
}
